import type React from 'react'
import { useEffect } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import type { WrapStyle } from '@/lib/object-effects/types/wrap-style'
import { OFFSET_SIDES } from '../constants/wrap-offsets'
import {
  aggregateCommonOffset,
  commonOffsetFromObj,
  offsetStringsFromObj,
  offsetValue,
} from '../helpers/offset-display'
import { normWrapStyle } from '../helpers/wrap-style-sides'
import type { WrapCarrier, WrapTarget } from '../types/wrap-offset'
import type { useCommonOffsetInput } from './use-common-offset-input'
import type { useSideOffsetInputs } from './use-side-offset-inputs'

type UseWrapOffsetSyncArgs = {
  targets: WrapTarget[]
  shapeManagerRef: React.RefObject<ShapeManagerRef | null> | undefined
  setStyleByTargetId: React.Dispatch<React.SetStateAction<Map<string, WrapStyle>>>
  sideInputs: ReturnType<typeof useSideOffsetInputs>
  commonInput: ReturnType<typeof useCommonOffsetInput>
}

/** Re-reads every target's committed wrap style and offsets into the panel's display state
 *  whenever they change, without clobbering an input the user is typing in. */
export function useWrapOffsetSync({
  targets,
  shapeManagerRef,
  setStyleByTargetId,
  sideInputs,
  commonInput,
}: UseWrapOffsetSyncArgs) {
  // Sync token: every selected target's id + committed wrapStyle + committed offsets, sorted by id
  // so mere reordering of the same selection never re-triggers this. Keyed this way (not on
  // activeObjects directly) so this doesn't re-run — and clobber an in-progress edit — on every
  // parent render.
  const syncToken = targets
    .map(
      (t) =>
        `${t.id}:${normWrapStyle(t.obj)}:${OFFSET_SIDES.map(({ field }) => offsetValue(t.obj, field)).join(',')}`
    )
    .sort()
    .join('|')

  useEffect(() => {
    if (targets.length === 0) {
      setStyleByTargetId(new Map())
      sideInputs.reset()
      commonInput.reset()
      return
    }

    const currentByTarget = targets.map((t): { target: WrapTarget; current: WrapCarrier } => ({
      target: t,
      current: shapeManagerRef?.current?.getShapes().find((s) => s.id === t.id) ?? t.obj,
    }))

    setStyleByTargetId(
      new Map(currentByTarget.map(({ target, current }) => [target.id, normWrapStyle(current)]))
    )

    sideInputs.syncFromTargets(
      currentByTarget.map(({ current }) => offsetStringsFromObj(current, normWrapStyle(current)))
    )

    if (!commonInput.commonInputFocused.current) {
      const perTarget = currentByTarget.map(({ current }) =>
        commonOffsetFromObj(current, normWrapStyle(current))
      )

      commonInput.showAggregate(aggregateCommonOffset(perTarget))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on syncToken only, see above
  }, [syncToken])
}
